// app/javascript/controllers/power_up_controller.js
import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static values = {
    gameId: Number,
    roundId: Number,
    isGuest: Boolean,
  };

  static targets = [
    "modalToggle",
    "modal",
    "modalTitle",
    "modalIcon",
    "modalBody",
    "countriesContainer",
    "country1",
    "country2",
    "powerupButton",
    "powerupCount",
    "confirmButton",
    "confirmationContainer",
    "upgradeContainer",
  ];

  // Store power-up results for reuse
  powerUpResults = {};
  currentPowerUpKey = null;
  usedPowerUps = {}; // Track which power-ups have been used

  connect() {
    // Add animation class to modal box
    if (this.hasModalTarget) {
      const modalBox = this.modalTarget.querySelector(".modal-box");
      if (modalBox) {
        modalBox.classList.add("modal-enter");
      }
    }
  }

  activate(event) {
    const button = event.currentTarget;
    const powerUpKey = button.dataset.powerUpKey;
    this.currentPowerUpKey = powerUpKey;

    // Check if user is guest
    if (this.isGuestValue) {
      this.showUpgradePrompt();
      return;
    }

    // Add animation to button
    if (this.hasPowerupButtonTarget) {
      this.powerupButtonTarget.classList.add("power-up-activate");
      setTimeout(() => {
        this.powerupButtonTarget.classList.remove("power-up-activate");
      }, 1500);
    }

    // If this power-up was already used, show the results directly
    if (this.usedPowerUps[powerUpKey] && this.powerUpResults[powerUpKey]) {
      this.showPowerUpResult(powerUpKey, this.powerUpResults[powerUpKey]);
      return;
    }

    // Otherwise show confirmation modal
    this.showConfirmation(powerUpKey);
  }

  showUpgradePrompt() {
    // Set modal title and icon for upgrade prompt
    this.modalTitleTarget.textContent = "Account Required";
    this.modalIconTarget.textContent = "🔒";

    // Update modal body text with upgrade message
    this.modalBodyTarget.textContent =
      "Power-ups are available after creating a free account.";

    // Hide confirmation and countries containers, show upgrade container
    this.confirmationContainerTarget.classList.add("hidden");
    this.countriesContainerTarget.classList.add("hidden");
    this.upgradeContainerTarget.classList.remove("hidden");

    // Show modal
    this.modalToggleTarget.checked = true;
  }

  showConfirmation(powerUpKey) {
    // Set modal title and icon for confirmation
    this.modalTitleTarget.textContent = "Use Power-Up?";
    this.modalIconTarget.textContent = "🔮";

    // Update modal body text with confirmation message
    if (powerUpKey === "fifty_fifty") {
      this.modalBodyTarget.textContent =
        "Do you want to use the 50/50 power-up to reveal two possible countries?";
    }

    // Show confirmation container, hide countries container and upgrade container
    this.confirmationContainerTarget.classList.remove("hidden");
    this.countriesContainerTarget.classList.add("hidden");
    this.upgradeContainerTarget.classList.add("hidden");

    // Show modal
    this.modalToggleTarget.checked = true;
  }

  confirmPowerUp() {
    const powerUpKey = this.currentPowerUpKey;

    if (!powerUpKey) return;

    console.log("Fetching power-up", powerUpKey);

    fetch(
      `/games/${this.gameIdValue}/rounds/${this.roundIdValue}/power_ups/${powerUpKey}`,
      {
        method: "POST",
        headers: {
          "X-CSRF-Token": document.querySelector("[name='csrf-token']").content,
          "Content-Type": "application/json",
        },
      }
    )
      .then((response) => {
        if (!response.ok) {
          return response.json().then((data) => {
            if (data.require_upgrade) {
              this.showUpgradePrompt();
              throw new Error("Upgrade required");
            }
            throw new Error(data.error || "Server error");
          });
        }
        return response.json();
      })
      .then((data) => {
        console.log("Power-up data", data);
        if (data.countries) {
          // Mark this power-up as used and store the results
          this.usedPowerUps[powerUpKey] = true;
          this.powerUpResults[powerUpKey] = data.countries;

          this.showPowerUpResult(powerUpKey, data.countries);
          console.log("Powerup count", data.powerupCount);
          this.powerupCountTarget.textContent = data.powerupCount;
        } else {
          this.showError(data.error || "Power-up failed");
        }
      })
      .catch((error) => {
        console.error("Power-up error", error);
        if (error.message !== "Upgrade required") {
          this.showError("An error occurred while using the power-up");
        }
      });
  }

  showPowerUpResult(powerUpKey, data) {
    if (powerUpKey === "fifty_fifty") {
      // Set modal title and icon
      this.modalTitleTarget.textContent = "50/50 Power-Up";
      this.modalIconTarget.textContent = "🌍";

      // Update modal body text
      this.modalBodyTarget.textContent = "It's one of these countries:";

      // Hide confirmation container, show countries container
      this.confirmationContainerTarget.classList.add("hidden");
      this.countriesContainerTarget.classList.remove("hidden");

      // Set country options
      const country1Span = this.country1Target.querySelector("span");
      const country2Span = this.country2Target.querySelector("span");

      if (country1Span && country2Span) {
        // Handle both array of strings and array of objects format
        if (typeof data[0] === "object") {
          country1Span.textContent = data[0].name;
          country2Span.textContent = data[1].name;

          // Add data attributes for zoom functionality
          this.country1Target.dataset.action = "click->power-up#zoomToCountry";
          this.country2Target.dataset.action = "click->power-up#zoomToCountry";
          this.country1Target.dataset.countrySlug = data[0].slug;
          this.country2Target.dataset.countryName = data[0].name;
          this.country2Target.dataset.countrySlug = data[1].slug;
          this.country1Target.dataset.countryName = data[1].name;
        } else {
          country1Span.textContent = data[0];
          country2Span.textContent = data[1];

          // Add data attributes for zoom functionality
          this.country1Target.dataset.action = "click->power-up#zoomToCountry";
          this.country2Target.dataset.action = "click->power-up#zoomToCountry";
          this.country1Target.dataset.countryName = data[0];
          this.country2Target.dataset.countryName = data[1];
        }
      }

      // Show modal
      this.modalToggleTarget.checked = true;
    }
  }

  zoomToCountry(event) {
    // Get country slug directly if available
    const countrySlug = event.currentTarget.dataset.countrySlug;
    const countryName =
      event.currentTarget.dataset.countryName ||
      event.currentTarget.querySelector("span")?.textContent.trim();

    if (!countrySlug && !countryName) {
      console.error(
        "No country information found in element:",
        event.currentTarget
      );
      return;
    }

    // Use slug directly or convert name to slug
    const slug = countrySlug || countryName.toLowerCase().replace(/\s+/g, "-");

    console.log("Zooming to country:", countryName, "with slug:", slug);

    // Fetch country bounds from the country bounds controller
    fetch(`/country_bounds/${encodeURIComponent(slug)}`)
      .then((response) => {
        if (!response.ok) {
          throw new Error(
            `Server returned ${response.status}: ${response.statusText}`
          );
        }
        return response.json();
      })
      .then((data) => {
        if (data.bounds) {
          console.log("Received bounds:", data.bounds);

          // Dispatch an event with the bounds for the map controller to handle
          document.dispatchEvent(
            new CustomEvent("map:zoom-to-bounds", {
              detail: {
                bounds: data.bounds,
                source: "power-up",
                countryName: countryName,
              },
            })
          );

          // Close the modal
          this.modalToggleTarget.checked = false;

          // Ensure the map is visible
          document.dispatchEvent(new CustomEvent("map-toggle:open"));
        } else {
          console.error("No bounds found in response:", data);
        }
      })
      .catch((error) => {
        console.log(encodeURIComponent(slug));
        console.error("Error fetching country bounds:", error);
      });
  }

  showError(message) {
    // Set modal title and icon for error
    this.modalTitleTarget.textContent = "Power-Up Failed";
    this.modalIconTarget.textContent = "❌";

    // Update modal body text with error
    this.modalBodyTarget.textContent = message;

    // Hide countries container and confirmation container
    this.countriesContainerTarget.classList.add("hidden");
    this.confirmationContainerTarget.classList.add("hidden");

    // Show modal
    this.modalToggleTarget.checked = true;
  }
}
